/* eslint-disable max-len */
import { isEmpty } from 'lodash';

const SPECIAL_GIFT_CONDITIONS = {
  VIP_EXPERIENCE: 'vip_experience',
  NOTHING: 'nothing',
  PURCHASE_DISCOUNT: 'purchase_discount',
  FRIENDS_FAMILY_FALLBACK: 'friends_family_fallback',
  SERVICE_REWARD_OIL_CHANGE: 'service_reward_oil_change',
  SALES_REWARD_TEST_DRIVE: 'sales_reward_test_drive',
  PURCHASE_DISCOUNT_PLUS_SERVICE: 'purchase_discount_plus_service',
  PURCHASE_DISCOUNT_PLUS_SALES: 'purchase_discount_plus_sales',
  FRIENDS_FAMILY_PLUS_SERVICE: 'friends_family_plus_service',
  FRIENDS_FAMILY_PLUS_SALES: 'friends_family_plus_sales',
  PURCHASE_DISCOUNT_VIP_EXPERIENCE_PLUS_SERVICE: 'purchase_discount_vip_experience_plus_service',
  PURCHASE_DISCOUNT_VIP_EXPERIENCE_PLUS_SALES: 'purchase_discount_vip_experience_plus_sales'
};

const PIPELINE_STAGES = [
  {
    condition: (ctx) =>
      !ctx.hasTheyGet && !ctx.hasAnyReward && !ctx.hasFriendsFamily && ctx.hasYouGet,
    result: SPECIAL_GIFT_CONDITIONS.VIP_EXPERIENCE
  },
  {
    condition: (ctx) =>
      !ctx.hasTheyGet && !ctx.hasAnyReward && !ctx.hasFriendsFamily && !ctx.hasYouGet,
    result: SPECIAL_GIFT_CONDITIONS.NOTHING
  },
  {
    condition: (ctx) => ctx.hasTheyGet && !ctx.hasAnyReward,
    result: SPECIAL_GIFT_CONDITIONS.PURCHASE_DISCOUNT
  },
  {
    condition: (ctx) => !ctx.hasTheyGet && !ctx.hasAnyReward && ctx.hasFriendsFamily,
    result: SPECIAL_GIFT_CONDITIONS.FRIENDS_FAMILY_FALLBACK
  },
  {
    condition: (ctx) => ctx.hasServiceReward && !ctx.hasTheyGet && !ctx.hasFriendsFamily,
    result: SPECIAL_GIFT_CONDITIONS.SERVICE_REWARD_OIL_CHANGE
  },
  {
    condition: (ctx) => ctx.hasSalesReward && !ctx.hasTheyGet && !ctx.hasFriendsFamily,
    result: SPECIAL_GIFT_CONDITIONS.SALES_REWARD_TEST_DRIVE
  },
  {
    condition: (ctx) => ctx.hasTheyGet && ctx.hasServiceReward && Number(ctx.hasYouGet) === 0,
    result: SPECIAL_GIFT_CONDITIONS.PURCHASE_DISCOUNT_PLUS_SERVICE
  },
  {
    condition: (ctx) => ctx.hasTheyGet && ctx.hasSalesReward && Number(ctx.hasYouGet) === 0,
    result: SPECIAL_GIFT_CONDITIONS.PURCHASE_DISCOUNT_PLUS_SALES
  },
  {
    condition: (ctx) => ctx.hasFriendsFamily && ctx.hasServiceReward,
    result: SPECIAL_GIFT_CONDITIONS.FRIENDS_FAMILY_PLUS_SERVICE
  },
  {
    condition: (ctx) => ctx.hasFriendsFamily && ctx.hasSalesReward,
    result: SPECIAL_GIFT_CONDITIONS.FRIENDS_FAMILY_PLUS_SALES
  },
  {
    condition: (ctx) => ctx.hasTheyGet && ctx.hasYouGet && ctx.hasServiceReward,
    result: SPECIAL_GIFT_CONDITIONS.PURCHASE_DISCOUNT_VIP_EXPERIENCE_PLUS_SERVICE
  },
  {
    condition: (ctx) => ctx.hasTheyGet && ctx.hasYouGet && ctx.hasSalesReward,
    result: SPECIAL_GIFT_CONDITIONS.PURCHASE_DISCOUNT_VIP_EXPERIENCE_PLUS_SALES
  }
];

const buildPipelineContext = (item) => {
  const { refer, reward } = item;
  const { they_get: theyGet, you_get: youGet, fnf_pricing: fnfPricing } = refer || {};
  const rewardType = reward?.type;
  const hasReward = !isEmpty(reward);

  const theyGetAmount = Number(theyGet) || 0;
  const youGetAmount = Number(youGet) || 0;

  return {
    hasYouGet: youGetAmount > 0,
    hasTheyGet: theyGetAmount > 0,
    hasFriendsFamily: fnfPricing,
    hasServiceReward: hasReward && rewardType === 'service',
    hasSalesReward: hasReward && rewardType === 'sales',
    hasAnyReward: (hasReward && rewardType === 'service') || (hasReward && rewardType === 'sales'),
    theyGetAmount,
    youGetAmount
  };
};

const getSpecialGiftCardCondition = (item) => {
  if (!item) return SPECIAL_GIFT_CONDITIONS.NOTHING;

  const context = buildPipelineContext(item);

  for (const stage of PIPELINE_STAGES) {
    if (stage.condition(context)) return stage.result;
  }

  return context.hasYouGet
    ? SPECIAL_GIFT_CONDITIONS.VIP_EXPERIENCE
    : SPECIAL_GIFT_CONDITIONS.NOTHING;
};

const getRewardTitle = (item) => {
  const itemType = item?.type;

  const { they_get: theyGet, you_get: youGet } = item.refer;
  const rewards = item?.reward || {};
  const rewardValue = rewards?.win_value || 0;
  const rewardType = rewards?.type || '';

  const theyGetAmount = Number(theyGet) || 0;
  const youGetAmount = Number(youGet) || 0;
  const rewardAmount = Number(rewardValue) || 0;

  const condition = getSpecialGiftCardCondition(item);

  const isService = rewardType === 'service';
  const rewardText = isService ? 'Oil Change' : 'Test Drive';
  const rewardBonus = isService
    ? `${rewardAmount}% Off ${rewardText}`
    : `$${rewardAmount} ${rewardText}`;

  const isReferrer = itemType === 'referrer';
  const isReferral = itemType === 'referral';

  switch (condition) {
    case SPECIAL_GIFT_CONDITIONS.VIP_EXPERIENCE:
      return isReferral ? `No Reward` : `Bird Dog $${youGetAmount}`;

    case SPECIAL_GIFT_CONDITIONS.NOTHING:
      return 'No Reward Program';

    case SPECIAL_GIFT_CONDITIONS.PURCHASE_DISCOUNT:
      return isReferrer ? `No Reward` : `Purchase Discount $${theyGetAmount}`;

    case SPECIAL_GIFT_CONDITIONS.FRIENDS_FAMILY_FALLBACK:
      return 'Friends & Family Pricing';

    case SPECIAL_GIFT_CONDITIONS.SERVICE_REWARD_OIL_CHANGE:
      return `${rewardAmount}% Off ${rewardText}`;

    case SPECIAL_GIFT_CONDITIONS.SALES_REWARD_TEST_DRIVE:
      return `$${rewardAmount} Test Drive (Gift Card)`;

    case SPECIAL_GIFT_CONDITIONS.PURCHASE_DISCOUNT_PLUS_SERVICE:
      return isReferral
        ? `Purchase Discount $${theyGetAmount} + ${rewardBonus}`
        : `Purchase Discount $${theyGetAmount}`;

    case SPECIAL_GIFT_CONDITIONS.PURCHASE_DISCOUNT_PLUS_SALES:
      return isReferral ? `${rewardBonus}` : `$${rewardAmount} Test Drive (Gift Card)`;

    case SPECIAL_GIFT_CONDITIONS.FRIENDS_FAMILY_PLUS_SERVICE:
      return `Friends & Family + ${rewardBonus}`;

    case SPECIAL_GIFT_CONDITIONS.FRIENDS_FAMILY_PLUS_SALES:
      return `${rewardBonus}`;

    case SPECIAL_GIFT_CONDITIONS.PURCHASE_DISCOUNT_VIP_EXPERIENCE_PLUS_SERVICE:
      return `Purchase Discount $${theyGetAmount} + ${rewardBonus}`;

    case SPECIAL_GIFT_CONDITIONS.PURCHASE_DISCOUNT_VIP_EXPERIENCE_PLUS_SALES:
      return `${rewardBonus}`;

    default:
      return 'Reward Program';
  }
};

export { getRewardTitle };
